import { OrderPageLoading } from "@/components/loaders"
import {
  StatusOrderClient,
  PixChargeDueCard,
  DeliveryPickupCodeCard,
  CancelScheduledOrderAction,
} from "@/components/orders"
import { PaymentMethod } from "@/components/orders/PaymentMethod"
import { PixQrOverlay } from "@/components/cart/confirm-order/PixQrOverlay"
import { CartIcon, CurrencyIcon, OrderIcon, Pin, Warning } from "@/icons"
import { AddressSummary, Callout, ButtonLink, Card, DescriptionList, Heading, KeyValueRow, PageContainer, Price, SectionHeader, Text } from "@/components/ui"
import { OrderItemLine, OrderItemList } from "@/components/orders/OrderItemLine"
import { toOrderItemLineProps } from "@/components/orders/order-item-line-props"
import { getStoredCompany } from "@/lib/company-storage"
import type { PixQrData } from "@/lib/checkout-saga"
import { orderDiscountRows } from "@/lib/order-discount-rows"
import { orderFeeRows } from "@/lib/order-fee-rows"
import { cashChangeFor, orderTotalValue } from "@/lib/order-total"
import {
  canRequestCancellation,
  getCancellationDeniedReason,
  getCurrentStatus,
  getLatestOpenPixCharge,
  hasCancellationDenied,
  getScheduledReadyAt,
  getTakeoutTime,
  isAwaitingPixPayment,
  TERMINAL_STATUSES,
} from "@/lib/order-status"
import { formatScheduledFor } from "@/lib/scheduling/format"
import { getTenantTimezone } from "@/lib/scheduling/tenant-timezone"
import { PaymentMethod as PaymentMethodType } from "@/types"
import { OrderResponse, OrderSagaStatusResponse } from "@/types/external"
import { OrderStatusType } from "@/types/order-status"
import { format } from "date-fns"
import { usePageBackground } from "@/hooks/use-page-background"
import { useMerchantCustomerConfig } from "@/hooks/use-merchant-customer-config"
import { useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { apiCustomer } from "@/api"
import { getMerchantId } from "@/lib/company-storage"
import { OrderType } from "@/types/order-type"
import { OrderRatingPrompt } from "@/components/orders/OrderRatingPrompt"
import { useOrderFeedbacks } from "@/hooks/use-order-feedbacks"


/**
 * How many times to retry a failed initial fetch before giving up.
 * Each attempt waits INITIAL_RETRY_DELAY_MS, so the total window is
 * ~INITIAL_RETRY_ATTEMPTS × INITIAL_RETRY_DELAY_MS ≈ 28s.
 *
 * This covers the async-materialization race: the order is created via an
 * `order.received` event, so GET /orders/:id can 404 for several seconds
 * immediately after the checkout POST returns the order ID.
 */
const INITIAL_RETRY_ATTEMPTS = 14
const INITIAL_RETRY_DELAY_MS = 2000

export function OrderPage() {
  const { id: orderId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [order, setOrder] = useState<OrderResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const hasLoadedOnce = useRef(false)
  const initialRetryCount = useRef(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    hasLoadedOnce.current = false
    initialRetryCount.current = 0

    async function fetchOrder() {
      const company = getStoredCompany()

      if (!company || !orderId) {
        navigate("/profile/orders", { replace: true })
        return
      }

      try {
        const { data: orderResponse } = await apiCustomer(`/orders/${orderId}`)
        hasLoadedOnce.current = true
        initialRetryCount.current = 0
        setOrder(orderResponse)

        const lastStatus = orderResponse.events?.[orderResponse.events.length - 1]?.status
        if (lastStatus && TERMINAL_STATUSES.has(lastStatus as OrderStatusType)) {
          if (intervalRef.current !== null) clearInterval(intervalRef.current)
        }
      } catch (error) {
        console.error(error)

        if (hasLoadedOnce.current) {
          return
        }

        initialRetryCount.current += 1
        if (initialRetryCount.current < INITIAL_RETRY_ATTEMPTS) {
          return
        }

        setLoading(false)
        navigate("/profile/orders", { replace: true })
        return
      } finally {
        if (hasLoadedOnce.current) {
          setLoading(false)
        }
      }
    }

    let upgradedToPolling = false

    async function fetchOrderWithUpgrade() {
      await fetchOrder()
      if (hasLoadedOnce.current && !upgradedToPolling) {
        upgradedToPolling = true
        if (intervalRef.current !== null) clearInterval(intervalRef.current)
        intervalRef.current = setInterval(fetchOrder, 10000)
      }
    }

    fetchOrderWithUpgrade()
    intervalRef.current = setInterval(fetchOrderWithUpgrade, INITIAL_RETRY_DELAY_MS)

    return () => {
      if (intervalRef.current !== null) clearInterval(intervalRef.current)
    }
  }, [orderId, navigate])

  const awaitingPayment = isAwaitingPixPayment(order)
  const [pixData, setPixData] = useState<PixQrData | null>(null)
  const [showPix, setShowPix] = useState(false)

  useEffect(() => {
    if (!awaitingPayment || !orderId) {
      setPixData(null)
      setShowPix(false)
      return
    }

    let cancelled = false
    async function fetchPixData() {
      try {
        const { data } = await apiCustomer.get<OrderSagaStatusResponse>(
          `/orders/${orderId}/saga-status`,
        )
        if (cancelled) return
        if (data.pixQrCode && data.pixExpiresAt) {
          setPixData({
            qrCode: data.pixQrCode,
            qrCodeBase64: data.pixQrCodeBase64 ?? "",
            txid: data.pixTxid ?? "",
            expiresAt: data.pixExpiresAt,
          })
        } else {
          setPixData(null)
        }
      } catch {
        if (!cancelled) setPixData(null)
      }
    }

    fetchPixData()
    return () => {
      cancelled = true
    }
  }, [awaitingPayment, orderId])

  const latestOpenCharge = getLatestOpenPixCharge(order)
  const [showChargeQr, setShowChargeQr] = useState(false)

  useEffect(() => {
    if (!latestOpenCharge) setShowChargeQr(false)
  }, [latestOpenCharge])

  usePageBackground()

  const orderMerchantId = order?.OrderMerchants?.[0]?.MerchantId ?? null
  const merchantId = orderMerchantId ?? getMerchantId()
  const { cancellationRequestEnabled } = useMerchantCustomerConfig(merchantId)
  const { feedbacks, remember } = useOrderFeedbacks(orderId ? [orderId] : [])

  if (loading) return <OrderPageLoading />
  if (!order) return null

  const itemsSubtotal = order.items.reduce((acc, item) => acc + item.totalPrice.value, 0)
  const discountRows = orderDiscountRows(order)
  const feeRows = orderFeeRows(order)
  const displayId = order.displayId ? order.displayId.toUpperCase() : "N/A"
  const paymentMethod = order.payments.methods[0].method as PaymentMethodType
  const changeFor = cashChangeFor(order)
  const cancellationDenied = hasCancellationDenied(order)
  const cancellationDeniedReason = getCancellationDeniedReason(order)
  const scheduledReadyAt = getScheduledReadyAt(order)
  const scheduledForLabel = scheduledReadyAt
    ? formatScheduledFor(scheduledReadyAt, new Date(), getTenantTimezone())
    : null
  const paymentRefunds = order.payments.refunds ?? []
  const orderCancelled = getCurrentStatus(order) === "CANCELLED"

  return (
    <>
      <PageContainer top="header-gap" bottom="chrome" className="flex flex-col gap-4">
        <header className="flex flex-col gap-1">
          <Heading level={1} size="section">
            Pedido #{displayId}
          </Heading>
          <Text tone="muted">
            Realizado em {format(order.createdAt, "dd/MM 'às' HH:mm")}
          </Text>
        </header>

        <StatusOrderClient
          order={order}
          canResumePix={awaitingPayment && !!pixData}
          onShowQr={() => setShowPix(true)}
        />

        <DeliveryPickupCodeCard order={order} />

        <OrderRatingPrompt order={order} feedback={feedbacks[order.id]} onRated={remember} variant="card" />

        {latestOpenCharge && (
          <PixChargeDueCard
            charge={latestOpenCharge}
            onShowQr={() => setShowChargeQr(true)}
          />
        )}

        <Card as="section" padding="lg">
          <SectionHeader icon={CartIcon} title="Itens do pedido" />
          <OrderItemList>
            {order.items.map((item) => (
              <OrderItemLine
                key={item.id}
                {...toOrderItemLineProps(item, { withImage: true, scaleOptionPrices: true, hideFreePrice: true })}
                priceTone="emphasis"
              />
            ))}
          </OrderItemList>
        </Card>

        {order.ExtraInfo ? (
          <Card as="section" padding="lg">
            <SectionHeader icon={OrderIcon} title="Observações" className="mb-2" />
            <Text tone="muted">{order.ExtraInfo}</Text>
          </Card>
        ) : null}

        <Card as="section" padding="lg">
          <SectionHeader icon={CurrencyIcon} title="Resumo dos valores" className="mb-3" />
          <DescriptionList>
            <KeyValueRow label="Método de pagamento" value={<PaymentMethod paymentMethod={paymentMethod} />} />
            {changeFor !== null ? (
              <KeyValueRow label="Troco para" value={<Price value={changeFor} />} />
            ) : null}
            <KeyValueRow label="Subtotal" value={<Price value={itemsSubtotal} />} />
            {feeRows.map((row) => (
              <KeyValueRow
                key={row.key}
                label={row.label}
                value={<Price value={Math.max(row.value, 0)} free />}
              />
            ))}
            {discountRows.map((row) => (
              <KeyValueRow
                key={row.key}
                label={row.label}
                value={<Price value={row.value} sign="minus" tone="secondary" />}
              />
            ))}
            {paymentRefunds.map((refund, index) => {
              const isFinalCancellationRefund =
                orderCancelled && index === paymentRefunds.length - 1
              return (
                <KeyValueRow
                  key={`refund-${index}`}
                  label={isFinalCancellationRefund ? "Estorno" : "Estorno parcial"}
                  value={<Price value={refund.value} sign="minus" tone="danger" weight="semibold" />}
                />
              )
            })}
            <KeyValueRow
              emphasis="total"
              label="Total"
              value={<Price value={orderTotalValue(order)} tone="secondary" />}
            />
          </DescriptionList>
        </Card>

        <Card as="section" padding="lg">
          <SectionHeader
            icon={Pin}
            title={
              order.type === OrderType.DELIVERY
                ? "Endereço de entrega"
                : order.type === "INDOOR"
                  ? "Consumo no local"
                  : "Retirada"
            }
            className="mb-3"
          />
          {scheduledForLabel ? (
            <Text weight="semibold" tone="secondary" className="mb-2">
              {scheduledForLabel}
            </Text>
          ) : null}
          {order.type === OrderType.DELIVERY ? (
            <div className="flex flex-col gap-1">
              <AddressSummary address={order.delivery.deliveryAddress} />
              <Text size="sm" tone="muted" className="mt-1">
                Entrega estimada para {format(order.delivery.estimatedDeliveryDateTime, "HH:mm")}
              </Text>
            </div>
          ) : (
            <div className="space-y-2">
              <Text weight="semibold" tone="default">
                {order.type === "INDOOR" ? "Deverá ficar pronto às" : "Retirada às"} {getTakeoutTime(order)}
              </Text>
              <ButtonLink to="/info" variant="link" tone="primary" size="auto">
                Ver informações do estabelecimento
              </ButtonLink>
            </div>
          )}
        </Card>

        {cancellationDenied && (
          <Callout tone="warning" icon={Warning} title="Cancelamento negado" titleAs="h2" role="none">
            {cancellationDeniedReason ? (
              <>
                <Text as="span" weight="semibold" tone="inherit">
                  Motivo:
                </Text>{" "}
                {cancellationDeniedReason}
              </>
            ) : (
              "A loja não pôde cancelar o pedido."
            )}
          </Callout>
        )}

        <CancelScheduledOrderAction order={order} onCancelled={setOrder} />

        {canRequestCancellation(order) && cancellationRequestEnabled && (
          <div className="flex justify-center py-3">
            <ButtonLink
              to={`/profile/orders/${order.id}/problem`}
              variant="link"
              tone="primary"
              size="auto"
            >
              Estou com um problema
            </ButtonLink>
          </div>
        )}
      </PageContainer>

      {showPix && pixData && (
        <PixQrOverlay
          open={showPix}
          orderId={order.id}
          qrCode={pixData.qrCode}
          qrCodeBase64={pixData.qrCodeBase64}
          expiresAt={pixData.expiresAt}
          onConfirmed={() => setShowPix(false)}
          onClose={() => setShowPix(false)}
        />
      )}

      {showChargeQr && latestOpenCharge && (
        <PixQrOverlay
          open={showChargeQr}
          orderId={order.id}
          qrCode={latestOpenCharge.qrCode}
          qrCodeBase64={latestOpenCharge.qrCodeBase64}
          expiresAt={latestOpenCharge.expiresAt}
          verifyMode="openCharge"
          chargeId={latestOpenCharge.chargeId}
          onConfirmed={() => {
            setShowChargeQr(false)
            void apiCustomer(`/orders/${order.id}`)
              .then(({ data }) => setOrder(data))
              .catch(() => undefined)
          }}
          onClose={() => setShowChargeQr(false)}
        />
      )}
    </>
  )
}
